import {
  auth,
  firestore,
  firestoreDb,
  storageRef,
  functions,
} from "@/config/firebase";
import router from "@/config/router";
import { loaderStart, loaderDone } from "@/services/Loader";
import { showMessage } from "@/services/MessageService";
import { i18n } from "@/i18n";
import store from "@/store";
export default {
  /**
   * Register course
   * @param {Object} participantData contains the eventID, participantID and courseID to register a course
   * Used in Program view in /views/Program/Program.vue
   */
  async registerCourse({ _ }: any, participantData) {
    loaderStart();
    try {
      let isRegister = false;
      const registerCourse = functions.httpsCallable("registerCourse");
      const courses = await firestoreDb
        .collection("events")
        .doc(participantData.eventID)
        .collection("participants")
        .doc(participantData.participantID)
        .collection("courses")
        .get();
      courses.docs.forEach((course) => {
        if (course.id === participantData.courseID) {
          isRegister = true;
        }
      });
      if (!isRegister) {
        const result = await registerCourse({
          courseID: participantData.courseID,
          eventID: participantData.eventID,
          participantID: participantData.participantID,
        });
        if (result.data === "courseWithPrice") {
          const clearCart = functions.httpsCallable("clearShoppingCart");
          await clearCart({ eventId: participantData.eventID });
          const addProduct = functions.httpsCallable("addProduct");
          await addProduct({
            eventId: participantData.eventID,
            productDocumentPath: `events/${participantData.eventID}/program/${participantData.courseID}`,
            quantity: 1,
          });
          router.push({ name: "CourseCheckout" });
        }
        if (result.data === "soldOut") {
          showMessage({
            title: i18n.global.t("program.registerCourse.soldOutError.title"),
            message: i18n.global.t(
              "program.registerCourse.soldOutError.message"
            ),
            type: "error",
          });
        }
        if (result.data === "succeeded") {
          showMessage({
            title: i18n.global.t(
              "program.registerCourse.registerSuccess.title"
            ),
            message: i18n.global.t(
              "program.registerCourse.registerSuccess.message"
            ),
            type: "success",
          });
          router.push({ name: "Courses" });
        }
      } else {
        showMessage({
          title: i18n.global.t(
            "program.registerCourse.alreadyRegisteredError.title"
          ),
          message: i18n.global.t(
            "program.registerCourse.alreadyRegisteredError.message"
          ),
          type: "error",
        });
      }
    } catch (error) {
      showMessage({
        title: i18n.global.t("program.registerCourse.registerError.title"),
        message: i18n.global.t("program.registerCourse.registerError.message"),
        type: "error",
      });
    } finally {
      loaderDone();
    }
  },
  /**
   * Register course
   * @param {Object} updateCoursePrice contains the eventID, coupon and courseID to update the price
   * Used in CoursePayment view in src\views\Program\CoursePayment.vue
   */
  async updateCoursePrice({ _ }: any, courseData) {
    loaderStart();
    try {
      const clearCart = functions.httpsCallable("clearShoppingCart");
      await clearCart({ eventId: courseData.eventID });
      const addProduct = functions.httpsCallable("addProduct");
      await addProduct({
        eventId: courseData.eventID,
        productDocumentPath: `events/${courseData.eventID}/program/${courseData.courseID}`,
        quantity: 1,
        coupon: courseData.coupon,
      });
    } catch (error) {
      showMessage({
        title: i18n.global.t("hotels.errorAddBooking.title"),
        message: i18n.global.t("hotels.errorAddBooking.message"),
        type: "error",
      });
    } finally {
      loaderDone();
    }
  },
  /**
   * Register course
   * @param {Object} participantData contains the eventID, couponID participantID and courseID to register a course with price 0
   * Used in CoursePayment view in src\views\Program\CoursePayment.vue
   */
  async registerCourseWithPriceZero({ _ }: any, participantData) {
    loaderStart();
    try {
      const stripeUpdateCouponRedemptions = functions.httpsCallable(
        "stripeUpdateCouponRedemptions"
      );
      const validRedemptions = await stripeUpdateCouponRedemptions({
        coupongId: participantData.couponID,
        eventId: participantData.eventID,
        coupongType: "course",
      });
      if (validRedemptions.data === "valid") {
        const registerCourseWhenPriceIsZero = functions.httpsCallable(
          "registerCourseWhenPriceIsZero"
        );
        const result = await registerCourseWhenPriceIsZero({
          eventID: participantData.eventID,
          participantID: participantData.participantID,
        });
        if (result.data === "soldOut") {
          showMessage({
            title: i18n.global.t("program.registerCourse.soldOutError.title"),
            message: i18n.global.t(
              "program.registerCourse.soldOutError.message"
            ),
            type: "error",
          });
        }
        if (result.data === "succeeded") {
          showMessage({
            title: i18n.global.t(
              "program.registerCourse.registerSuccess.title"
            ),
            message: i18n.global.t(
              "program.registerCourse.registerSuccess.message"
            ),
            type: "success",
          });
        }
        if (result.data === "error") {
          showMessage({
            title: i18n.global.t("program.registerCourse.registerError.title"),
            message: i18n.global.t(
              "program.registerCourse.registerError.message"
            ),
            type: "error",
          });
        }
      } else {
        showMessage({
          title: i18n.global.t("coupon.invalidCoupon"),
          message: i18n.global.t("coupon.invalidCouponDetail"),
          type: "warning",
        });
      }
    } catch (error) {
      showMessage({
        title: i18n.global.t("program.registerCourse.registerError.title"),
        message: i18n.global.t("program.registerCourse.registerError.message"),
        type: "error",
      });
    } finally {
      loaderDone();
      router.push({ name: "Courses" });
    }
  },
  /**
   * Download certificate of the course
   * @param {string} programID contains programId of the course to download the certificate
   */
  async downloadCourseCertificate({ _ }: any, { programID }) {
    const eventID = store.getters["user/eventData"].id;
    const generateUserCertificate = functions.httpsCallable(
      "generateUserCertificate"
    );
    const result = await generateUserCertificate({
      eventId: eventID,
      programId: programID,
    });
    if (result.data.certificate) {
      const certificate = await storageRef
        .child(result.data.certificate.certificatePath)
        .getDownloadURL();
      window.open(certificate, "_blank");
    }
  },
  /**
   * Donwload certificate of the event
   */
  async downloadEventCertificate({ _ }: any) {
    const eventID = store.getters["user/eventData"].id;
    const generateUserCertificate = functions.httpsCallable(
      "generateUserCertificate"
    );
    const result = await generateUserCertificate({
      eventId: eventID,
    });
    if (result.data.certificate) {
      const certificate = await storageRef
        .child(result.data.certificate.certificatePath)
        .getDownloadURL();
      window.open(certificate, "_blank");
    }
  },
  async downloadEventProgram({ _ }: any, { eventID }) {
    const eventProgram = store.getters["user/eventData"].programPDF;
    if (eventProgram) {
      const certificate = await storageRef
        .child(`events/${eventID}/programPDF`)
        .getDownloadURL();
      window.open(certificate, "_blank");
    }
  },
};
